// The snooker room around the table: sizes and where everything stands (pure data and maths, no three.js).
// RoomModel.ts builds the room from this; roomLayout.test.ts checks that the camera can't reach any of it.
//
// Coordinates as everywhere in the 3D scene: x and z are the physics plane (z = physics y), y is up, the floor is
// y = 0 (the table's legs stand on it), the ball centres are at BALL_CENTRE_Y.
//
// Why the room is this big: the camera orbits the cue ball (Game.UpdateCamera) at up to 15.5 (the mouse wheel's
// limit, Events.onMouseWheel), from any side and from just above the cloth to almost straight down, and the cue ball
// can be anywhere on the bed. With the near plane at 1 and a 60 degree vertical field of view, the near plane's
// corners reach up to ~2.4 further on a very wide window. So the walls stand beyond 15.5 + 2.4 from every point the
// cue ball can reach, the ceiling above 7.93 + 15.5 + 2.4, and the furniture stands between that limit and the walls.
// The lamp hangs over the table, inside the camera's range: it fades out as the camera rises towards it
// (lampOpacity), so it can never be in front of the cue ball or cut by the near plane.
import {BALL_RADIUS, TABLE} from "./shared/rules";
import {BALL_CENTRE_Y, TABLE_CENTRE} from "./tableShapes";

export type V3 = [number, number, number];
export interface Box { name: string; min: V3; max: V3; }

/** The camera limits that the room is sized for (Events.ts, Game.ts). */
export const CAMERA = {
    maxDistance: 15.5,
    near: 1,
    fovY: 60,
    /** a very wide window (32:9); the near plane gets wider with the aspect */
    maxAspect: 3.6,
    /** the keyboard keeps the vertical angle within (-1.5 - one step, 1.5 + one step), the mouse within (PI/16, PI/2) */
    minVertical: -1.5 - 10 / 360,
    maxVertical: Math.PI / 2,
    /** the lowest angle mouse look allows (Events.onMouseMove) */
    mouseMinVertical: Math.PI / 16,
};

const CX = TABLE_CENTRE[0], CZ = TABLE_CENTRE[1];

/** Where the cue ball centre can be (the bed inside the cushions). */
export const CUE_REACH = {
    xMin: TABLE.xMin + BALL_RADIUS, xMax: TABLE.xMax - BALL_RADIUS,
    zMin: TABLE.yMin + BALL_RADIUS, zMax: TABLE.yMax - BALL_RADIUS,
};

const HALF_X = 27, HALF_Z = 30;
/** Inside faces of the walls, the floor and the ceiling. */
export const ROOM = {
    xMin: CX - HALF_X, xMax: CX + HALF_X,
    zMin: CZ - HALF_Z, zMax: CZ + HALF_Z,
    floor: 0,
    ceiling: 27,
};

/** Wainscoting: dark wood panels up to the dado rail; wallpaper above. */
export const WAINSCOT = {
    top: 9.0,          // top of the dado rail
    railHeight: 0.55,
    railDepth: 0.35,
    skirtingHeight: 1.1,
    skirtingDepth: 0.22,
    panelDepth: 0.1,
    corniceHeight: 0.8,
    corniceDepth: 0.45,
};

/** The canopy over the table (visual only; the scene's lights are unchanged). */
export const LAMP = {
    cx: CX, cz: CZ,
    halfX: 3.3, halfZ: 6.3,
    bottom: 16.5,      // lower edge of the shade, ~1 m above the cloth at the table's scale
    top: 17.8,
};

/** Fully visible with the camera this far below the shade, gone this far below it. */
export const LAMP_FADE = {start: 2.6, end: 1.1};

/**
 * Opacity of the lamp (shade, diffuser, rods) for a camera at height y: 1 well below the shade, 0 from
 * LAMP_FADE.end below it upwards, so the camera never sees the lamp from above or close enough to cut it.
 */
export function lampOpacity(cameraY: number): number {
    const t = (LAMP.bottom - LAMP_FADE.end - cameraY) / (LAMP_FADE.start - LAMP_FADE.end);
    return Math.max(0, Math.min(1, t));
}

const W = ROOM;
/** Wall-mounted pieces and furniture, as boxes (RoomModel builds the detail inside each box). */
export const FURNITURE = {
    // far wall (+z), ahead of the player at the break: the scoreboard and the club's sign, low (just above the dado
    // rail) and out to the sides. The middle of the wall, and anything high on it, is at the top centre of the view
    // when looking along the table, behind the HUD's score panel (QA F4); out here they show beside and below it.
    // (Screen left in the break view is +x.)
    scoreboard: {name: "scoreboard", min: [CX - 25, 9.8, W.zMax - 0.9], max: [CX - 15, 15.9, W.zMax]} as Box,
    sign: {name: "sign", min: [CX + 15, 9.8, W.zMax - 0.3], max: [CX + 24.5, 15.2, W.zMax]} as Box,
    // right wall (+x): the cue rack, a chair either side
    cueRack: {name: "cue rack", min: [W.xMax - 1.1, 1.0, CZ - 4.5], max: [W.xMax, 14.6, CZ + 4.5]} as Box,
    chairA: {name: "chair", min: [W.xMax - 4.4, 0, CZ - 15.2], max: [W.xMax - 0.4, 9.2, CZ - 11.2]} as Box,
    chairB: {name: "chair", min: [W.xMax - 4.4, 0, CZ + 11.2], max: [W.xMax - 0.4, 9.2, CZ + 15.2]} as Box,
    // left wall (-x): a long upholstered bench, and the door
    bench: {name: "bench", min: [W.xMin + 0.3, 0, CZ - 9], max: [W.xMin + 4.1, 7.6, CZ + 9]} as Box,
    door: {name: "door", min: [W.xMin, 0, CZ - 25.5], max: [W.xMin + 0.5, 19.5, CZ - 16.5]} as Box,
    // back wall (-z, behind the player at the break): a landscape painting and an old club photograph, also low and
    // to the sides (High quality only)
    painting: {name: "painting", min: [CX - 25, 10.0, W.zMin], max: [CX - 15, 15.6, W.zMin + 0.3]} as Box,
    photo: {name: "photo", min: [CX + 15, 10.2, W.zMin], max: [CX + 23, 14.8, W.zMin + 0.3]} as Box,
};

export const FURNITURE_BOXES: Box[] = Object.values(FURNITURE);

/** Camera position for the cue ball at (bx, bz), like Game.UpdateCamera. */
export function cameraPosition(bx: number, bz: number, h: number, v: number, d: number): V3 {
    return [Math.cos(v) * Math.sin(h) * d + bx, Math.sin(v) * d + BALL_CENTRE_Y, Math.cos(v) * Math.cos(h) * d + bz];
}

/**
 * The camera and its near plane's corners when it looks at the cue ball (three.js lookAt with up = +y), for the
 * widest window. Anything the camera could cut through is at one of these points or between them.
 */
export function nearPlanePoints(bx: number, bz: number, h: number, v: number, d: number, aspect = CAMERA.maxAspect): V3[] {
    const c = cameraPosition(bx, bz, h, v, d);
    let f: V3 = [bx - c[0], BALL_CENTRE_Y - c[1], bz - c[2]];
    let fl = Math.hypot(f[0], f[1], f[2]);
    if (fl < 1e-9) { f = [-Math.sin(h), 0, -Math.cos(h)]; fl = 1; }   // d = 0: any direction
    f = [f[0] / fl, f[1] / fl, f[2] / fl];
    // right = f x up, up' = right x f
    let r: V3 = [-f[2], 0, f[0]];
    const rl = Math.hypot(r[0], r[2]) || 1;
    r = [r[0] / rl, 0, r[2] / rl];
    const u: V3 = [r[1] * f[2] - r[2] * f[1], r[2] * f[0] - r[0] * f[2], r[0] * f[1] - r[1] * f[0]];
    const hy = Math.tan(CAMERA.fovY * Math.PI / 360) * CAMERA.near, hx = hy * aspect;
    const pts: V3[] = [c];
    for (const sx of [-1, 1]) {
        for (const sy of [-1, 1]) {
            pts.push([c[0] + f[0] * CAMERA.near + r[0] * hx * sx + u[0] * hy * sy,
                c[1] + f[1] * CAMERA.near + r[1] * hx * sx + u[1] * hy * sy,
                c[2] + f[2] * CAMERA.near + r[2] * hx * sx + u[2] * hy * sy]);
        }
    }
    return pts;
}

export function insideBox(p: V3, b: {min: V3; max: V3}, pad = 0): boolean {
    return p[0] > b.min[0] - pad && p[0] < b.max[0] + pad && p[1] > b.min[1] - pad && p[1] < b.max[1] + pad
        && p[2] > b.min[2] - pad && p[2] < b.max[2] + pad;
}

/** The lamp as a box: the shade and the rods up to the ceiling. */
export const LAMP_BOX: Box = {name: "lamp", min: [LAMP.cx - LAMP.halfX, LAMP.bottom, LAMP.cz - LAMP.halfZ],
    max: [LAMP.cx + LAMP.halfX, ROOM.ceiling, LAMP.cz + LAMP.halfZ]};
